import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Spinner, BrandLockup } from "@opero/ui";
import { getErrorMessage } from "@digiits-agency/polaris-sdk";
import { polaris } from "../lib/polaris-client";
import { AuthModalShell } from "./AuthModalShell";
import { ResendVerification } from "./ResendVerification";
import type { VerifyEmailResponse } from "../features/workspace-setup/types";

interface VerifyEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Module-level guard — survives React 19 StrictMode unmount+remount, which
// would otherwise fire the verify call twice and burn the single-use token.
const inFlight = new Map<string, Promise<VerifyEmailResponse>>();

export function VerifyEmailModal({ isOpen, onClose }: VerifyEmailModalProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [status, setStatus] = useState<"verifying" | "success" | "error">(
    "verifying",
  );

  useEffect(() => {
    if (!isOpen) return;
    if (!token) {
      setStatus("error");
      return;
    }
    setStatus("verifying");

    let cached = inFlight.get(token);
    if (!cached) {
      // SDK namespacer types verifyEmail as Promise<void>; the endpoint
      // actually returns { setupToken, expiresIn, nextStep }. Drop to the
      // typed http client so we can read the body.
      cached = polaris.http
        .post<
          { data?: VerifyEmailResponse } | VerifyEmailResponse
        >("/v1/auth/identity/verify-email", undefined, { params: { token } })
        .then((res) => {
          const body = res.data as
            | { data?: VerifyEmailResponse }
            | VerifyEmailResponse;
          return (
            (body as { data?: VerifyEmailResponse }).data ??
            (body as VerifyEmailResponse)
          );
        });
      inFlight.set(token, cached);
    }

    let cancelled = false;
    cached
      .then((payload) => {
        if (cancelled) return;
        if (payload?.setupToken) {
          navigate("/setup", {
            replace: true,
            state: {
              setupToken: payload.setupToken,
              expiresIn: payload.expiresIn,
            },
          });
          return;
        }
        setStatus("success");
      })
      .catch((err) => {
        // Don't keep a rejected promise in the cache — a Resend gives the user
        // a new token, but back/forward to the same URL should re-attempt.
        inFlight.delete(token);
        if (cancelled) return;
        console.error("[verify-email]", getErrorMessage(err, "verify failed"), err);
        setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, token, navigate]);

  return (
    <AuthModalShell isOpen={isOpen} onClose={onClose} brand={<BrandLockup />}>
      <div className="px-7 pb-8 pt-4 text-center space-y-4">
        {status === "verifying" && (
          <>
            <Spinner size="lg" />
            <p className="text-sm text-slate-500">
              Verifying your email...
            </p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-accent-500/10 flex items-center justify-center">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-accent-500"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              Email Verified
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Your email has been verified. Sign in to finish setting up
              your workspace.
            </p>
            <button
              type="button"
              onClick={() => navigate("/login?verified=1", { replace: true })}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-500 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-600"
            >
              Continue to sign in
            </button>
          </>
        )}
        {status === "error" && (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-red-500/10 flex items-center justify-center">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-red-500"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              Verification Failed
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              This link is invalid or has expired.
            </p>
            <div className="pt-2">
              <ResendVerification />
            </div>
          </>
        )}
      </div>
    </AuthModalShell>
  );
}
